/**
 * Builds the theme from the colour profile in effect and provides it.
 *
 * It starts from the look this browser last applied, so a member whose tenant
 * has its own colours does not see Meridian flash on every load; the shell then
 * applies the profile the server resolved (ShellLook). The browser's
 * `theme-color` follows the primary.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { buildTheme, paletteFor, ThemeProvider, type ProfileColours } from '../ui';
import {
  browserStorage,
  LookContext,
  rememberColours,
  rememberedColours,
  sameColours,
  type LookValue,
} from './look';

export function LookProvider({ children }: { children: ReactNode }) {
  const [colours, setColours] = useState<ProfileColours>(() => rememberedColours(browserStorage()));
  const theme = useMemo(() => buildTheme(paletteFor(colours)), [colours]);

  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.palette.primary.main);
  }, [theme]);

  const applyColours = useCallback((next: ProfileColours) => {
    setColours((current) => (sameColours(current, next) ? current : next));
    rememberColours(browserStorage(), next);
  }, []);

  const value = useMemo<LookValue>(() => ({ colours, applyColours }), [colours, applyColours]);

  return (
    <LookContext.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </LookContext.Provider>
  );
}
